import { getLinkAccounts, userLinkUnbind } from "@apis/http/api";
import { UserLinkAccountItemResponsePlatform } from "@apis/generated/openapi";
import type { ResponseOf } from "@apis/http/typedRoute";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { Status } from "@models/common";
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useEffect } from "react";

import { RootState } from "./store";

export type LinkedAccount = NonNullable<ResponseOf<"GET/api/users/link/accounts">>[number];
export type LinkedAccountPlatform = NonNullable<LinkedAccount["platform"]>;

export const TIKTOK_PLATFORM = UserLinkAccountItemResponsePlatform.TIKTOK;

interface LinkedAccountState {
  status: Status;
  unbindingIds: number[];
  items: LinkedAccount[];
}

const initialState: LinkedAccountState = {
  status: "idle",
  unbindingIds: [],
  items: [],
};

export const fetchLinkedAccounts = createAsyncThunk("linkedAccount/fetch", async () => {
  const data = await getLinkAccounts();
  return data ?? [];
});

export const unbindLinkedAccount = createAsyncThunk("linkedAccount/unbind", async (id: number) => {
  await userLinkUnbind({ id });
  return id;
});

export const linkedAccountSlice = createSlice({
  name: "linkedAccount",
  initialState,
  reducers: {
    resetLinkedAccounts: () => initialState,
    upsertLinkedAccount: (state, action: PayloadAction<LinkedAccount>) => {
      const next = action.payload;
      const idx = state.items.findIndex((a) => a.id !== undefined && a.id === next.id);
      if (idx >= 0) {
        state.items[idx] = next;
      } else {
        state.items.unshift(next);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLinkedAccounts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLinkedAccounts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchLinkedAccounts.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(unbindLinkedAccount.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.unbindingIds.includes(id)) {
          state.unbindingIds.push(id);
        }
      })
      .addCase(unbindLinkedAccount.fulfilled, (state, action) => {
        const id = action.payload;
        state.unbindingIds = state.unbindingIds.filter((x) => x !== id);
        state.items = state.items.filter((a) => a.id !== id);
      })
      .addCase(unbindLinkedAccount.rejected, (state, action) => {
        const id = action.meta.arg;
        state.unbindingIds = state.unbindingIds.filter((x) => x !== id);
      });
  },
});

export const { resetLinkedAccounts, upsertLinkedAccount } = linkedAccountSlice.actions;

export const selectLinkedAccountState = (s: RootState) => s.linkedAccountReducer;
export const selectLinkedAccounts = (s: RootState) => s.linkedAccountReducer.items;
export const selectLinkedAccountsStatus = (s: RootState) => s.linkedAccountReducer.status;
export const selectLinkedAccountsLoaded = (s: RootState) => s.linkedAccountReducer.status === "succeeded";

export const selectTiktokLinkedAccounts = createSelector([selectLinkedAccounts], (items) =>
  items.filter((a) => a.platform === TIKTOK_PLATFORM),
);

export const selectTiktokCreatorLinkedAccounts = createSelector([selectTiktokLinkedAccounts], (accounts) =>
  accounts.filter((a) => a.accountUserType === "CREATOR"),
);
export const selectHasLinkedAccount = (s: RootState) => s.linkedAccountReducer.items.length > 0;
export const selectIsUnbinding = (id: number) => (s: RootState) => s.linkedAccountReducer.unbindingIds.includes(id);

export const useLinkedAccounts = (autoFetch = true) => {
  const dispatch = useAppDispatch();
  const { items, status, unbindingIds } = useAppSelector(selectLinkedAccountState);

  useEffect(() => {
    if (autoFetch && status === "idle") {
      void dispatch(fetchLinkedAccounts());
    }
  }, [autoFetch, status, dispatch]);

  return {
    items,
    status,
    unbindingIds,
    refetch: () => dispatch(fetchLinkedAccounts()),
  };
};

export const linkedAccountReducer = linkedAccountSlice.reducer;
